import I18n from 'I18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useFetchAllPU from './useFetchAllPU';
import usePUViewedIdxDB from './usePUViewedIdxDB';
import Raven from 'raven-js';
import { impactLevelToTagText } from '../utils';
const NEW_TO_YOU_MONTH_RANGE = 1;

// filter for new to you page
const toFilterSet = filter => new Set(Array.isArray(filter) ? filter : [filter]);
const getFilterFunction = params => targetPU => {
  return Object.entries(params).filter(([__, value]) => value !== null).map(([key, value]) => {
    switch (key) {
      case 'endStageDate':
        return pu => pu.stageDate <= value;
      case 'featureGroup':
        return pu => pu.featureGroups.some(featureGroup => toFilterSet(value).has(featureGroup));
      case 'impactLevels':
        return pu => toFilterSet(value).has(impactLevelToTagText(pu.rollout.impactLevel));
      case 'product':
        return pu => pu.rollout.products.some(product => toFilterSet(value).has(product));
      case 'query':
        return pu => pu.title.toLowerCase().includes(value.toLowerCase());
      case 'startStageDate':
        return pu => pu.stageDate >= value;
      default:
        return __ => true;
    }
  }).every(filterFunc => filterFunc(targetPU));
};
const useMarkPUViewed = ({
  userId,
  paramsForGetRolloutProductUpdates,
  pageSize,
  currentOffset
}) => {
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const startStageDate = useMemo(() => I18n.moment().subtract(NEW_TO_YOU_MONTH_RANGE, 'months').startOf('day').utc().valueOf(), []);
  // generate new to you params
  const params = useMemo(() => {
    const emptyFilters = {
      endReleaseDate: null,
      endStageDate: null,
      enrollmentStatus: null,
      featureGroup: null,
      isFavorite: null,
      limit: null,
      offset: null,
      product: null,
      query: null,
      recommended: null,
      rolloutState: null,
      rolloutType: null,
      sortReleaseDate: null,
      sortStageDate: null,
      startReleaseDate: null,
      startStageDate: null,
      impactLevels: null
    };
    return Object.assign({}, emptyFilters, {
      startStageDate
    });
  }, [startStageDate]);
  const {
    allProductUpdates,
    isGetProductUpdateLoading,
    getAllPUError
  } = useFetchAllPU(params);
  const [unviewedPUIds, setUnviewedPUIds] = useState(new Set());
  const db = usePUViewedIdxDB(userId, startStageDate);
  const processAllScheduledViewedPUs = useCallback(async () => {
    if (!db.isSupported) {
      return null;
    }
    setIsProcessing(true);
    await db.processAllScheduledViewedPUs();
    const _unViewedPUIds = await db.getAllUnviewedPUIds();
    setUnviewedPUIds(_unViewedPUIds);
    setIsProcessing(false);
  }, [db]);
  useEffect(() => {
    if (db.isSupported && allProductUpdates.length) {
      setIsSettingUp(true);
      db.setup(allProductUpdates).then(async () => {
        // update unviewedPUId
        const _unViewedPUIds = await db.getAllUnviewedPUIds();
        setUnviewedPUIds(_unViewedPUIds);
        return;
      }).catch(error => {
        Raven.captureMessage('Initializing new to you tab failed', {
          extra: {
            userId,
            error
          }
        });
      }).finally(() => {
        setIsSettingUp(false);
      });
    }
  }, [allProductUpdates, db, userId]);
  const isLoading = isSettingUp || isProcessing || db.isLoading || isGetProductUpdateLoading;
  const filteredNewToYouProductUpdates = useMemo(() => allProductUpdates.filter(getFilterFunction(paramsForGetRolloutProductUpdates)).filter(pu => unviewedPUIds.has(pu.id)), [allProductUpdates, paramsForGetRolloutProductUpdates, unviewedPUIds]);
  return useMemo(() => {
    return {
      isSupported: true,
      getAllPUError,
      isLoading,
      startStageDate,
      newToYouProductUpdates: filteredNewToYouProductUpdates.slice(currentOffset, currentOffset + pageSize),
      newToYouTotalCount: filteredNewToYouProductUpdates.length,
      unviewedPUIds,
      schedulePUViewed: db.schedulePUViewed,
      processAllScheduledViewedPUs
    };
  }, [isLoading, getAllPUError, startStageDate, filteredNewToYouProductUpdates, currentOffset, pageSize, unviewedPUIds, db.schedulePUViewed, processAllScheduledViewedPUs]);
};
export default useMarkPUViewed;